'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Shield, Building2, Loader2 } from 'lucide-react';
import type { ReactElement } from 'react';

interface WorkOSSSOButtonProps {
  organizationId?: string;
  connectionId?: string;
  provider?: string;
  email?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
}

export function WorkOSSSOButton({
  organizationId,
  connectionId,
  provider,
  email,
  variant = 'default',
  size = 'default',
  className = '',
}: WorkOSSSOButtonProps): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSSO = async (): Promise<void> => {
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/auth/initiate-sso', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organizationId,
          connectionId,
          provider,
          email,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error ?? 'Failed to initiate SSO');
      }

      const { authorizationURL } = await response.json();
      
      // Redirect to WorkOS SSO
      window.location.href = authorizationURL;
    } catch (error) {
      console.error('SSO initiation failed:', error);
      alert(`SSO failed: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonText = (): string => {
    if (isLoading) return 'Connecting...';
    
    if (provider) {
      return `Sign in with ${provider}`;
    }
    
    if (organizationId) {
      return '[Coming Soon]'; // TODO: Revert to 'Sign in with SSO' when WorkOS is fully implemented
    }
    
    return '[Coming Soon]'; // TODO: Revert to 'Sign in with SSO' when WorkOS is fully implemented
  };

  const getProviderIcon = (): ReactElement => {
    if (provider) {
      // You can add specific provider icons here
      return <Shield className="size-4" />;
    }
    
    return <Building2 className="size-4" />;
  };

  return (
    <Button
      onClick={handleSSO}
      disabled={isLoading}
      variant={variant}
      size={size}
      className={`w-full ${className}`}
    >
      {isLoading ? (
        <Loader2 className="size-4 mr-2 animate-spin" />
      ) : (
        getProviderIcon()
      )}
      <span className="ml-2">{getButtonText()}</span>
    </Button>
  );
}
